/**
 * Review Sessions API Client
 * 
 * Fetches review session data from .cursor/reports folder.
 * Uses static manifest file for directory listing.
 * Files are served via symlink in public/cursor-reports.
 */

export interface ReviewSession {
  id: string;
  name: string;
  created: string | null;
  lastUpdated: string | null;
  fileCount: number;
  files: string[];
}

export interface SessionsManifest {
  lastUpdated: string;
  sessions: ReviewSession[];
}

/**
 * Fetch all review sessions from the manifest
 */
export async function getReviewSessions(): Promise<ReviewSession[]> {
  try {
    const response = await fetch('/cursor-reports/sessions-manifest.json');
    if (!response.ok) {
      console.warn('Review sessions manifest not found. Run /copy-to-review to create one.');
      return [];
    }
    const manifest: SessionsManifest = await response.json();
    return manifest.sessions || [];
  } catch (error) {
    console.error('Failed to fetch review sessions:', error);
    return [];
  }
}

/**
 * Fetch content of a specific file from a review session
 */
export async function getFileContent(sessionId: string, filePath: string): Promise<string> {
  const url = `/cursor-reports/review-session-${sessionId}/${filePath}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${filePath}: ${response.status}`);
  }
  return response.text();
}

/**
 * Get file type based on extension
 */
export function getFileType(filePath: string): string {
  const ext = filePath.split('.').pop()?.toLowerCase() || '';
  const typeMap: Record<string, string> = {
    'ts': 'typescript',
    'tsx': 'typescript',
    'js': 'javascript',
    'jsx': 'javascript',
    'py': 'python',
    'md': 'markdown',
    'json': 'json',
    'css': 'css',
    'scss': 'scss',
    'html': 'html',
  };
  return typeMap[ext] || 'text';
}

